import PortalStatus from './PortalStatus';
import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  PortalShell,
  Surface,
  Button,
  Field,
  TextInput,
  TextArea,
  Select,
  ErrorSummary,
  SectionHeader,
  SaveState,
  StatusChip,
  LangToggle,
  useT,
  useFontClass,
  toBn,
} from "components/admissions/portalUi";
import { higherCompulsory } from "components/admissions/SubjectFields";
import { usePortalApplication } from "./usePortalApplication";
import { buildSections, sectionStatus, validateSection, getIn, setIn } from "./portalForm";

/**
 * One section of the application at a time.
 *
 * Two columns only for genuinely short, related fields (the Bangla/English name
 * pairs); addresses and anything needing explanation run full width. Validation
 * happens on Save & continue rather than while typing, the message appears
 * beside its own field, and the summary at the top moves focus straight to it.
 *
 * Every button here writes to the server before it navigates. Back and Save &
 * exit skip validation — a half-finished section is exactly what a draft is
 * for — but Save & continue will not move on until the section is valid and
 * the save has actually succeeded.
 */
export default function PortalSection() {
  const t = useT();
  const font = useFontClass();
  const navigate = useNavigate();
  const { step: stepParam } = useParams();

  const { access, refresh, app, answers, update, save, loading, loadError, saveState, savedAt, editable } =
    usePortalApplication();

  const [showSections, setShowSections] = useState(false);
  const [errors, setErrors] = useState([]);
  const [busy, setBusy] = useState(false);

  const sections = useMemo(
    () =>
      buildSections({
        institution: app?.institution,
        classApplied: app?.classApplied,
      }),
    [app]
  );

  const index = Math.min(Math.max(parseInt(stepParam, 10) || 1, 1), sections.length) - 1;
  const section = sections[index];

  // Review is its own screen; landing on it by number should go there.
  useEffect(() => {
    if (section?.review) navigate("/admissions-portal/review", { replace: true });
  }, [section, navigate]);

  // A new section starts at the top, which matters most on a phone.
  useEffect(() => {
    window.scrollTo({ top: 0 });
    setErrors([]);
  }, [index]);

  const set = (path) => (e) =>
    update(setIn(answers, path, e.target ? e.target.value : e));
  const val = (path) => getIn(answers, path) ?? "";
  const errFor = (id) => errors.find((e) => e.id === id)?.message;

  const goTo = (i) =>
    navigate(sections[i].review ? "/admissions-portal/review" : `/admissions-portal/section/${i + 1}`);

  /** Save without validating — used by Back and Save & exit. */
  const saveThen = async (after) => {
    setBusy(true);
    const err = await save();
    setBusy(false);
    if (err) {
      setErrors([{ id: "save-failed", message: err.message }]);
      return;
    }
    after();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const found = validateSection(section, answers, t);
    setErrors(found);
    if (found.length) return;

    setBusy(true);
    const err = await save();
    setBusy(false);
    // Never advance on a failed save: the next screen would look fine while
    // this section's answers were still only in the browser.
    if (err) {
      setErrors([{ id: "save-failed", message: err.message }]);
      return;
    }
    goTo(index + 1);
  };

  if (loading) {
    return (
      <PortalShell>
        <p className={`${font} text-[16px] text-muted`}>{t("লোড হচ্ছে…", "Loading…")}</p>
      </PortalShell>
    );
  }

  if (loadError) {
    return (
      <PortalShell>
        <Surface className="p-6">
          <p className={`${font} text-[16px] text-ink mb-4`}>{loadError}</p>
          <Link to="/admissions-portal" className={`${font} text-green underline`}>
            {t("ফিরে যান", "Back to admissions")}
          </Link>
        </Surface>
      </PortalShell>
    );
  }

  if (app && !editable) return <PortalStatus app={app} access={access} refresh={refresh} />;

  if (!section || section.review) return null;

  return (
    <PortalShell>
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <span className={`${font} text-[14px] text-muted`}>
          {t("আবেদন নম্বর", "Application")}{" "}
          <strong className="text-ink">{app?.applicationNo}</strong>
        </span>
        <LangToggle />
      </div>

      {!editable && (
        <Surface className="p-4 mb-6 border-gold">
          <p className={`${font} text-[15px] text-ink`}>
            {t(
              "এই আবেদনটি জমা দেওয়া হয়েছে, তাই এখন আর পরিবর্তন করা যাবে না।",
              "This application has been submitted, so it can no longer be changed."
            )}
          </p>
        </Surface>
      )}

      {app?.status === "NEEDS_CORRECTION" && app?.correctionNote && (
        <Surface className="p-4 mb-6 border-gold">
          <h2 className={`${font} text-[16px] font-bold text-green-dark mb-1`}>
            {t("বিদ্যালয়ের মন্তব্য", "Note from the school")}
          </h2>
          <p className={`${font} text-[15px] text-ink`}>{app.correctionNote}</p>
        </Surface>
      )}

      <SectionHeader
        step={index + 1}
        total={sections.length}
        title={t(section.bn, section.en)}
        onViewAll={() => setShowSections((v) => !v)}
      />

      {/* A readable section menu, not a row of tiny pills. */}
      {showSections && (
        <Surface className="p-2 mb-6">
          <ul>
            {sections.map((s, i) => (
              <li key={s.key}>
                <button
                  type="button"
                  onClick={() => saveThen(() => goTo(i))}
                  className={`${font} w-full flex items-center justify-between gap-3 text-left px-3.5 py-3 rounded-xl min-h-[52px] hover:bg-cream transition-colors ${
                    i === index ? "bg-cream" : ""
                  }`}
                >
                  <span className="text-[16px] text-ink">
                    <span className="text-muted mr-2">{i + 1}.</span>
                    {t(s.bn, s.en)}
                  </span>
                  <StatusChip status={sectionStatus(s, answers)} />
                </button>
              </li>
            ))}
          </ul>
        </Surface>
      )}

      <ErrorSummary errors={errors} className="mb-6" />

      <form onSubmit={onSubmit} noValidate>
        <fieldset disabled={!editable} className="border-none p-0 m-0">
          {section.key === "student" && (
            <StudentSection val={val} set={set} errFor={errFor} answers={answers} app={app} />
          )}
          {section.key === "family" && <FamilySection val={val} set={set} errFor={errFor} />}
          {section.key === "address" && (
            <AddressSection
              val={val}
              set={set}
              errFor={errFor}
              answers={answers}
              update={update}
              withEducation={section.education}
            />
          )}
          {section.key === "subjects" && (
            <SubjectsSection answers={answers} update={update} level={section.level} />
          )}
        </fieldset>

        {/* One strong action; the rest stay quiet. */}
        <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-3 sm:gap-4">
          <Button
            type="button"
            variant="secondary"
            className="sm:w-auto"
            disabled={busy || index === 0}
            onClick={() => saveThen(() => goTo(index - 1))}
          >
            ← {t("পূর্ববর্তী", "Back")}
          </Button>
          <Button type="submit" className="sm:flex-1" disabled={busy || !editable}>
            {busy
              ? t("সংরক্ষণ হচ্ছে…", "Saving…")
              : `${t("সংরক্ষণ করে পরবর্তী ধাপ", "Save & continue")} →`}
          </Button>
          <button
            type="button"
            disabled={busy}
            onClick={() => saveThen(() => navigate("/admissions-portal"))}
            className={`${font} text-[15px] text-muted underline bg-transparent border-none cursor-pointer min-h-[48px] px-2 disabled:opacity-50`}
          >
            {t("সংরক্ষণ করে বের হন", "Save & exit")}
          </button>
        </div>

        <div className="mt-4">
          <SaveState
            state={saveState}
            at={savedAt ? savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : null}
          />
        </div>
      </form>
    </PortalShell>
  );
}

/* ------------------------------------------------------------------ sections */

function StudentSection({ val, set, errFor, app }) {
  const t = useT();
  const font = useFontClass();
  return (
    <>
      <Surface className="p-5 sm:p-7 mb-6">
        <h2 className={`${font} text-[17px] font-bold text-green-dark mb-1`}>
          {t("যে শ্রেণিতে আবেদন", "Applying for")}
        </h2>
        <p className={`${font} text-[16px] text-ink`}>
          {app?.classApplied}
          {app?.groupApplied ? ` — ${app.groupApplied}` : ""}
        </p>
        <p className={`${font} text-[13.5px] text-muted mt-1`}>
          {t(
            "শ্রেণি বদলাতে হলে বিদ্যালয় অফিসে যোগাযোগ করুন।",
            "To change the class, please contact the school office."
          )}
        </p>
      </Surface>

      <Surface className="p-5 sm:p-7 mb-6">
        <fieldset className="border-none p-0 m-0 mb-7">
          <legend className={`${font} text-[17px] font-bold text-green-dark mb-4 p-0`}>
            {t("শিক্ষার্থীর নাম", "Student's name")}
          </legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              id="applicant.nameBn"
              label={t("বাংলায়", "In Bangla")}
              required
              error={errFor("applicant.nameBn")}
            >
              <TextInput
                id="applicant.nameBn"
                value={val("applicant.nameBn")}
                onChange={set("applicant.nameBn")}
                error={errFor("applicant.nameBn")}
                className="font-bn"
              />
            </Field>
            <Field id="applicant.nameEn" label={t("ইংরেজিতে (বড় হাতের অক্ষরে)", "In English (capitals)")}>
              <TextInput
                id="applicant.nameEn"
                value={val("applicant.nameEn")}
                onChange={set("applicant.nameEn")}
                className="uppercase"
              />
            </Field>
          </div>
          <p className={`${font} text-[13.5px] text-muted mt-3`}>
            {t(
              "শিক্ষার্থীর কাগজপত্রে যেভাবে লেখা আছে, ঠিক সেভাবে লিখুন।",
              "Write the name exactly as it appears on the student's documents."
            )}
          </p>
        </fieldset>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            id="dob"
            label={t("জন্ম তারিখ", "Date of birth")}
            hint={t("জন্ম নিবন্ধন অনুযায়ী", "As on the birth registration certificate")}
            required
            error={errFor("dob")}
          >
            <TextInput
              id="dob"
              type="date"
              value={val("dob")}
              onChange={set("dob")}
              error={errFor("dob")}
              max={new Date().toISOString().slice(0, 10)}
            />
          </Field>
          <Field id="religion" label={t("ধর্ম", "Religion")}>
            <Select id="religion" value={val("religion")} onChange={set("religion")}>
              <option value="">{t("বেছে নিন…", "Choose…")}</option>
              <option value="Islam">{t("ইসলাম", "Islam")}</option>
              <option value="Hinduism">{t("হিন্দু", "Hinduism")}</option>
              <option value="Buddhism">{t("বৌদ্ধ", "Buddhism")}</option>
              <option value="Christianity">{t("খ্রিস্টান", "Christianity")}</option>
              <option value="Prefer not to say">
                {t("উল্লেখ করতে ইচ্ছুক নই", "Prefer not to say")}
              </option>
            </Select>
          </Field>
          <Field id="nationality" label={t("জাতীয়তা", "Nationality")}>
            <TextInput
              id="nationality"
              value={val("nationality")}
              onChange={set("nationality")}
              placeholder={t("বাংলাদেশি", "Bangladeshi")}
            />
          </Field>
        </div>
      </Surface>
    </>
  );
}

function FamilySection({ val, set, errFor }) {
  const t = useT();
  const font = useFontClass();
  return (
    <>
      <Surface className="p-5 sm:p-7 mb-6">
        {[
          ["father", t("পিতার নাম", "Father's name")],
          ["mother", t("মাতার নাম", "Mother's name")],
        ].map(([who, legend], i) => (
          <fieldset key={who} className={`border-none p-0 m-0 ${i === 0 ? "mb-7" : ""}`}>
            <legend className={`${font} text-[17px] font-bold text-green-dark mb-4 p-0`}>
              {legend}
            </legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field
                id={`${who}.nameBn`}
                label={t("বাংলায়", "In Bangla")}
                required
                error={errFor(`${who}.nameBn`)}
              >
                <TextInput
                  id={`${who}.nameBn`}
                  value={val(`${who}.nameBn`)}
                  onChange={set(`${who}.nameBn`)}
                  error={errFor(`${who}.nameBn`)}
                  className="font-bn"
                />
              </Field>
              <Field id={`${who}.nameEn`} label={t("ইংরেজিতে (বড় হাতের অক্ষরে)", "In English (capitals)")}>
                <TextInput
                  id={`${who}.nameEn`}
                  value={val(`${who}.nameEn`)}
                  onChange={set(`${who}.nameEn`)}
                  className="uppercase"
                />
              </Field>
            </div>
          </fieldset>
        ))}
      </Surface>

      <Surface className="p-5 sm:p-7 mb-6">
        <h2 className={`${font} text-[17px] font-bold text-green-dark mb-4`}>
          {t("অভিভাবকের তথ্য", "Guardian's details")}
        </h2>

        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              id="guardian.relation"
              label={t("শিক্ষার্থীর সঙ্গে সম্পর্ক", "Relationship to the student")}
            >
              <Select
                id="guardian.relation"
                value={val("guardian.relation")}
                onChange={set("guardian.relation")}
              >
                <option value="">{t("বেছে নিন…", "Choose…")}</option>
                <option value="father">{t("পিতা", "Father")}</option>
                <option value="mother">{t("মাতা", "Mother")}</option>
                <option value="other">{t("অন্যান্য অভিভাবক", "Other guardian")}</option>
              </Select>
            </Field>
            <Field
              id="guardian.name"
              label={t("অভিভাবকের নাম", "Guardian's name")}
              hint={t(
                "পিতা বা মাতা হলে খালি রাখতে পারেন",
                "Leave blank if this is the father or mother"
              )}
            >
              <TextInput
                id="guardian.name"
                value={val("guardian.name")}
                onChange={set("guardian.name")}
                className="font-bn"
              />
            </Field>
            <Field
              id="guardian.occupation"
              label={t("পেশা", "Occupation")}
              required
              error={errFor("guardian.occupation")}
            >
              <TextInput
                id="guardian.occupation"
                value={val("guardian.occupation")}
                onChange={set("guardian.occupation")}
                error={errFor("guardian.occupation")}
                className="font-bn"
              />
            </Field>
            <Field id="guardian.monthlyIncome" label={t("মাসিক আয় (টাকা)", "Monthly income (Tk.)")}>
              <TextInput
                id="guardian.monthlyIncome"
                inputMode="numeric"
                value={val("guardian.monthlyIncome")}
                onChange={set("guardian.monthlyIncome")}
              />
            </Field>
          </div>

          {/* Full width: an address needs the room. */}
          <Field id="guardian.workAddress" label={t("কর্মস্থলের ঠিকানা", "Workplace address")}>
            <TextArea
              id="guardian.workAddress"
              rows={2}
              value={val("guardian.workAddress")}
              onChange={set("guardian.workAddress")}
              className="font-bn"
            />
          </Field>

          <Field
            id="guardian.phone"
            label={t("মোবাইল নম্বর", "Mobile number")}
            hint={t(
              "বিদ্যালয় এই নম্বরেই যোগাযোগ করবে। বাংলা বা ইংরেজি — যেকোনো সংখ্যায় লিখতে পারেন।",
              "The school will contact you on this number. Bangla or English digits are both fine."
            )}
            required
            error={errFor("guardian.phone")}
            className="sm:max-w-[320px]"
          >
            <TextInput
              id="guardian.phone"
              type="tel"
              inputMode="tel"
              value={val("guardian.phone")}
              onChange={set("guardian.phone")}
              error={errFor("guardian.phone")}
            />
          </Field>
        </div>
      </Surface>
    </>
  );
}

function AddressSection({ val, set, errFor, answers, update, withEducation }) {
  const t = useT();
  const font = useFontClass();
  const same = !!getIn(answers, "permanent.sameAsPresent");
  const rows = getIn(answers, "education") || [];

  const setRow = (i, key) => (e) =>
    update(
      setIn(
        answers,
        "education",
        rows.map((r, j) => (j === i ? { ...r, [key]: e.target.value } : r))
      )
    );

  return (
    <>
      <Surface className="p-5 sm:p-7 mb-6">
        <h2 className={`${font} text-[17px] font-bold text-green-dark mb-4`}>
          {t("ঠিকানা", "Address")}
        </h2>
        <div className="flex flex-col gap-5">
          <Field
            id="present.address"
            label={t("বর্তমান ঠিকানা", "Present address")}
            required
            error={errFor("present.address")}
          >
            <TextArea
              id="present.address"
              rows={3}
              value={val("present.address")}
              onChange={set("present.address")}
              error={errFor("present.address")}
              className="font-bn"
            />
          </Field>

          <Field
            id="present.phone"
            label={t("যোগাযোগ নম্বর", "Contact number")}
            hint={t("অভিভাবকের নম্বর থেকে আলাদা হলে", "If different from the guardian's number")}
            error={errFor("present.phone")}
            className="sm:max-w-[320px]"
          >
            <TextInput
              id="present.phone"
              type="tel"
              inputMode="tel"
              value={val("present.phone")}
              onChange={set("present.phone")}
              error={errFor("present.phone")}
            />
          </Field>

          <label
            htmlFor="permanent.sameAsPresent"
            className="flex gap-3.5 items-start rounded-xl p-3.5 border-[1.5px] border-line hover:bg-cream cursor-pointer transition-colors"
          >
            <input
              id="permanent.sameAsPresent"
              type="checkbox"
              checked={same}
              onChange={(e) => update(setIn(answers, "permanent.sameAsPresent", e.target.checked))}
              className="mt-1 w-5 h-5 cursor-pointer shrink-0 accent-[#005C40]"
            />
            <span className={`${font} text-[15.5px] text-ink leading-relaxed`}>
              {t("স্থায়ী ঠিকানা একই", "The permanent address is the same")}
            </span>
          </label>

          {!same && (
            <Field id="permanent.address" label={t("স্থায়ী ঠিকানা", "Permanent address")}>
              <TextArea
                id="permanent.address"
                rows={3}
                value={val("permanent.address")}
                onChange={set("permanent.address")}
                className="font-bn"
              />
            </Field>
          )}
        </div>
      </Surface>

      {withEducation && (
        <Surface className="p-5 sm:p-7 mb-6">
          <h2 className={`${font} text-[17px] font-bold text-green-dark mb-1`}>
            {t("শিক্ষাগত যোগ্যতা", "Academic history")}
          </h2>
          <p className={`${font} text-[14px] text-muted mb-4 leading-relaxed`}>
            {t(
              "সর্বশেষ পাবলিক পরীক্ষার তথ্য দিন। না দিয়েও সংরক্ষণ করা যাবে।",
              "Give the most recent public examination. You can save without it."
            )}
          </p>

          {rows.map((row, i) => (
            <fieldset key={i} className="border-[1.5px] border-line rounded-2xl p-4 mb-4">
              <legend className={`${font} text-[15px] font-semibold text-ink px-2`}>
                {t(`পরীক্ষা ${toBn(i + 1)}`, `Examination ${i + 1}`)}
              </legend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  ["exam", t("পরীক্ষার নাম", "Examination")],
                  ["groupYear", t("বিভাগ ও সাল", "Group and year")],
                  ["registration", t("রেজিস্ট্রেশন ও সেশন", "Registration and session")],
                  ["centreSerial", t("কেন্দ্র ও রোল", "Centre and roll")],
                  ["gpa", t("জিপিএ", "GPA")],
                  ["institution", t("প্রতিষ্ঠানের নাম", "Institution")],
                  ["board", t("বোর্ড", "Board")],
                ].map(([key, label]) => (
                  <Field key={key} id={`education.${i}.${key}`} label={label}>
                    <TextInput
                      id={`education.${i}.${key}`}
                      value={row[key] || ""}
                      onChange={setRow(i, key)}
                    />
                  </Field>
                ))}
              </div>
              <button
                type="button"
                onClick={() =>
                  update(setIn(answers, "education", rows.filter((_, j) => j !== i)))
                }
                className={`${font} text-[14px] text-red underline bg-transparent border-none cursor-pointer min-h-[44px] mt-2`}
              >
                {t("এই পরীক্ষাটি মুছুন", "Remove this examination")}
              </button>
            </fieldset>
          ))}

          <Button
            type="button"
            variant="secondary"
            disabled={rows.length >= 6}
            onClick={() => update(setIn(answers, "education", [...rows, {}]))}
          >
            + {t("পরীক্ষা যোগ করুন", "Add an examination")}
          </Button>
        </Surface>
      )}
    </>
  );
}

function SubjectsSection({ answers, update, level }) {
  const t = useT();
  const font = useFontClass();

  // Higher secondary always carries the same three compulsory subjects; filling
  // them in is a service, not a decision the family has to make.
  useEffect(() => {
    if (level === "higher" && getIn(answers, "subjects.compulsory") === undefined) {
      update(setIn(answers, "subjects.compulsory", [...higherCompulsory]));
    }
  }, [level, answers, update]);

  const lists = [
    ["compulsory", t("আবশ্যিক বিষয়", "Compulsory subjects")],
    ["optional", t("নৈর্বাচনিক / ঐচ্ছিক বিষয়", "Elective / optional subjects")],
  ];

  return (
    <Surface className="p-5 sm:p-7 mb-6">
      <h2 className={`${font} text-[17px] font-bold text-green-dark mb-1`}>
        {level === "higher"
          ? t("উচ্চ মাধ্যমিক", "Higher secondary")
          : t("মাধ্যমিক", "Secondary")}
      </h2>
      <p className={`${font} text-[14px] text-muted mb-5 leading-relaxed`}>
        {t(
          "বিদ্যালয়ের নির্দেশনা অনুযায়ী বিষয় লিখুন। চূড়ান্ত অনুমোদন বিদ্যালয় দেবে।",
          "Enter subjects as advised by the school. Final choices are subject to the school's approval."
        )}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {lists.map(([key, label]) => {
          const items = getIn(answers, `subjects.${key}`) || [];
          const write = (next) => update(setIn(answers, `subjects.${key}`, next));
          return (
            <div key={key}>
              <h3 className={`${font} text-[15px] font-semibold text-ink mb-3`}>{label}</h3>
              <div className="flex flex-col gap-3">
                {items.map((subject, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <label htmlFor={`subjects.${key}.${i}`} className="sr-only">
                      {label} {i + 1}
                    </label>
                    <TextInput
                      id={`subjects.${key}.${i}`}
                      value={subject}
                      maxLength={120}
                      onChange={(e) =>
                        write(items.map((s, j) => (j === i ? e.target.value : s)))
                      }
                      className="font-bn"
                    />
                    <button
                      type="button"
                      aria-label={t(`${label} ${i + 1} মুছুন`, `Remove ${label} ${i + 1}`)}
                      onClick={() => write(items.filter((_, j) => j !== i))}
                      className={`${font} text-[14px] text-muted underline bg-transparent border-none cursor-pointer min-h-[44px] px-1 shrink-0`}
                    >
                      {t("মুছুন", "Remove")}
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  disabled={items.length >= 20}
                  onClick={() => write([...items, ""])}
                  className={`${font} text-[15px] text-green font-semibold underline bg-transparent border-none cursor-pointer min-h-[44px] text-left disabled:opacity-50`}
                >
                  + {t("বিষয় যোগ করুন", "Add a subject")}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </Surface>
  );
}
